import { Core, Decorative } from "@faire/design-tokens";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IItemPromoPrice } from "@faire/web-api/indigofair/data/IItemPromoPrice";
import { FaireDate } from "@faire/web/common/FaireDate";
import {
  FaireDateRange,
  PresetDateFormat,
} from "@faire/web/common/FaireDateRange";
import { intlDate } from "@faire/web/common/localization/date-fns/intlDate";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { formatMoney } from "@faire/web/common/money";
import { Button } from "@faire/web/slate/Button";
import { Family } from "@faire/web/slate/Font/utils";
import { Add } from "@faire/web/slate/icons/foundation/Add";
import { Subtract } from "@faire/web/slate/icons/foundation/Subtract";
import { MeasurementInput } from "@faire/web/slate/Input/MeasurementInput";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Tooltip } from "@faire/web/slate/Tooltip";
import { Span, Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Money } from "@faire/web/ui/Money";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { showProductPromotions } from "@faire/retailer/lib/showProductPromotions";

interface ITooltipProps {
  quantityThreshold?: number;
}

interface IProps {
  className?: string;
  onChange?: (value: number) => void;
  label: string;
  value: number;
  isDisabled?: boolean;
  costSuffix?: IFaireMoney;
  promoPrice?: IItemPromoPrice;
  quantityThreshold?: number;
  isFirst?: boolean;
  shipDate?: FaireDateRange;
  orderByDate?: FaireDate;
}

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/QuantityPicker/QuantityPicker#QuantityPicker"
 */
const QuantityPickerBase: React.FC<IProps> = ({
  className,
  onChange,
  label,
  value,
  isDisabled,
  costSuffix,
  isFirst,
  quantityThreshold,
  promoPrice,
  shipDate,
  orderByDate,
}) => {
  const { strictLocalize } = useStrictLocalization();

  const handleSubtractValue = () => {
    onChange?.(value - 1);
  };

  const handleAddValue = () => {
    onChange?.(value + 1);
  };

  const handleCustomValue = (value?: number) => {
    onChange?.(value ?? 0);
  };

  return (
    <Container className={className} $isFirst={isFirst}>
      <LabelContainer>
        <FullWidthColumn>
          <CenterAlignedRow>
            <Spacer width="2x" />
            <StyledTypography
              variant="paragraphSansRegular"
              $isDisabled={isDisabled}
              data-test-id={`open-sizing-size-${label}`}
              color={isDisabled ? Core.text.subdued : undefined}
            >
              {label}
            </StyledTypography>

            {costSuffix ? (
              <>
                <Spacer width="1x" />
                <Typography
                  variant="labelSansRegular"
                  color={Core.text.subdued}
                  data-test-id={`open-sizing-${label}-variation-suffix`}
                >
                  (
                  {showProductPromotions(promoPrice) ? (
                    <>
                      <StrikeThrough>
                        {formatMoney({ money: costSuffix })}
                      </StrikeThrough>
                      <Span color={Decorative.shade.orange}>
                        {" "}
                        {formatMoney({
                          money: promoPrice.discounted_item_price,
                        })}
                      </Span>
                    </>
                  ) : (
                    <Money
                      money={promoPrice?.discounted_item_price ?? costSuffix}
                    />
                  )}
                  )
                </Typography>
              </>
            ) : undefined}
          </CenterAlignedRow>
          {shipDate ? (
            <Row>
              <Spacer width="2x" />
              <Typography
                variant="labelSansRegular"
                color={Core.text.subdued}
                data-test-id={`open-sizing-${label}-variation-shipping-suffix`}
              >
                {orderByDate ? (
                  <StrictLocalMsg
                    defaultMessage="Preorder {shippingDate}. Order must be placed by {orderByDate}."
                    description={{
                      text: "Shipping date range label for product options variations that have to be preordered",
                    }}
                    values={{
                      shippingDate:
                        shipDate?.formatPresetDateRange(
                          PresetDateFormat.ABBREVIATED_MMM_D
                        ) ?? "",
                      orderByDate: intlDate(
                        orderByDate.localDateObject,
                        "MMM d"
                      ),
                    }}
                  />
                ) : (
                  <StrictLocalMsg
                    defaultMessage="Preorder {shippingDate}"
                    description={{
                      text: "Shipping date range label for product options variations that have to be preordered",
                    }}
                    values={{
                      shippingDate:
                        shipDate?.formatPresetDateRange(
                          PresetDateFormat.ABBREVIATED_MMM_D
                        ) ?? "",
                    }}
                  />
                )}
              </Typography>
            </Row>
          ) : undefined}
        </FullWidthColumn>
      </LabelContainer>
      <InputContainer>
        <Row align="center">
          <StyledButton
            onClick={handleSubtractValue}
            $isDisabled={isDisabled}
            disabled={isDisabled}
          >
            <Subtract color={Core.icon.default} />
          </StyledButton>
          <StyledNumberInput
            precision={0}
            value={isDisabled ? 0 : value}
            onChange={handleCustomValue}
            $isDisabled={isDisabled}
            disabled={isDisabled}
            error={quantityThreshold ? value > quantityThreshold : false}
            data-test-id={`open-sizing-size-quantity-${label}`}
          />
          {quantityThreshold && value > quantityThreshold ? (
            <StyledTooltip
              contents={[
                {
                  body: strictLocalize(
                    {
                      defaultMessage:
                        "{quantityThreshold, plural, one{Only # piece left} other{Only # pieces left}}",
                      description: {
                        text: "total number of pieces left for the product",
                      },
                    },
                    DEPRECATED_backwardsCompatibleValues({ quantityThreshold })
                  ),
                },
              ]}
              position="top"
              isOpen
              primary
              width={150}
            />
          ) : undefined}
          <StyledButton
            onClick={handleAddValue}
            $isDisabled={isDisabled}
            disabled={isDisabled}
          >
            <Add color={Core.icon.default} />
          </StyledButton>
        </Row>
      </InputContainer>
    </Container>
  );
};

export const QuantityPicker = observer(QuantityPickerBase);

const Container = styled(Row).attrs({
  justify: "space-between",
  align: "center",
})<{ $isFirst?: boolean }>`
  width: 100%;
  padding: ${getSpacing(5, 0)};
  border: 1px solid ${Core.border.muted};
  margin-top: ${({ $isFirst }) => ($isFirst ? "unset" : "-1px")};
`;

const LabelContainer = styled(Row).attrs({
  align: "center",
})`
  width: 100%;
  margin-right: ${getSpacing("2x")};
`;

const FullWidthColumn = styled(Column)`
  width: 100%;
`;

const CenterAlignedRow = styled(Row).attrs({
  align: "center",
})`
  width: 100%;
`;

const StyledTypography = styled(Typography)<{ $isDisabled?: boolean }>`
  text-decoration: ${({ $isDisabled }) =>
    $isDisabled ? "line-through" : undefined};
`;

const InputContainer = styled(Row).attrs({
  justify: "flex-end",
  align: "center",
})`
  width: 140px;

  ${responsiveMediaQueries.mobile} {
    width: 150px;
  }
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Tooltip/Tooltip#Tooltip"
 */
const StyledTooltip = styled(Tooltip)<ITooltipProps>`
  top: -10px;
  left: -21px;

  div {
    align-items: center;
  }

  svg {
    display: none;
  }
`;

const StyledButton = styled(Button).attrs({
  variant: "secondary",
})<{
  $isDisabled?: boolean;
}>`
  width: 30px;
  height: 30px;
  margin: 0 4px;
  border: none;

  &:focus,
  &:focus-within {
    background: transparent;
  }

  // overriding attribute styles
  &:disabled {
    background-color: ${Core.surface.inverse};
    color: ${Core.text.subdued};
    border: none;
    svg {
      color: ${Core.border.muted};
    }
  }

  &:hover {
    background: ${({ $isDisabled }) =>
      $isDisabled ? "transparent" : Core.surface.subdued};

    svg {
      color: ${({ $isDisabled }) =>
        $isDisabled ? Core.icon.subdued : Core.icon.default};
      stroke-width: 1.5;
    }
  }
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Input/MeasurementInput/MeasurementInput#MeasurementInput"
 */
const StyledNumberInput = styled(MeasurementInput)<{
  $isDisabled?: boolean;
}>`
  width: 42px;
  height: 30px;
  padding: 0;

  input {
    border: solid 1px ${Core.border.muted};
    color: ${({ $isDisabled }) =>
      $isDisabled ? Core.text.subdued : Core.text.default};
    font-family: ${Family.sansSerif};
    background: transparent;
    font-size: 12px;
    font-weight: 200;
    padding: 0 2px;
    text-align: center;
    width: 30px;
  }
`;

const StrikeThrough = styled.span`
  text-decoration: line-through;
  color: ${Core.text.subdued};
`;
